import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit, faSave, faTimes, faHistory, faClipboardList,
  faFileText, faCheckCircle, faMoneyBillWave, faExclamationTriangle, faList,
  faLock, faCalendarAlt, faUser, faBuilding, faComments, faArchive, faBoxOpen, faRobot,
  faStickyNote, faPlus, faSpinner, faCertificate,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';
import SearchableSelect from '../../components/SearchableSelect';
import SearchableMultiSelect from '../../components/SearchableMultiSelect';
import BudgetTab from './components/BudgetTab';
import DocumentsTab from './components/DocumentsTab';
import ChecklistTab from './components/ChecklistTab';
import CautionsTab from './components/CautionsTab';
import SurveySlotsTab from './components/SurveySlotsTab';
import BriefTab from './components/BriefTab';
import AOTab from './components/AOTab';
import AttestationTab from './components/AttestationTab';
import ConfirmModal from './components/ConfirmModal';
import PaymentRelanceCard from './components/PaymentRelanceCard';
import { useModalDismiss } from '../../hooks/useModalDismiss';

const STEP_LABELS = {
  ao: 'Avant-Vente', setup: 'Setup', terrain: 'Terrain', reporting: 'Reporting', closing: 'Closing',
};

const STEP_ACCENT = {
  ao: '#991b1b', setup: '#f59e0b', terrain: '#10b981', reporting: '#0ea5e9', closing: '#8b5cf6',
};

const STEPS_ORDER = ['ao', 'setup', 'terrain', 'reporting', 'closing'];

const DEPT_STYLE = {
  define:    { bg: '#dbeafe', color: '#1e40af', label: 'Define' },
  voa:       { bg: '#fef9c3', color: '#a16207', label: 'Voa' },
  immersion: { bg: '#fee2e2', color: '#991b1b', label: 'Immersion' },
};

const TYPE_LABELS = {
  contrat: 'Contrat', appel_offre: 'Appel d\'Offre', consultation_restreinte: 'Consultation Restreinte',
};

const BASE_TABS = [
  { key: 'overview', label: 'Aperçu', icon: faClipboardList },
  { key: 'surveys', label: 'Enquêtes', icon: faList },
  { key: 'budget', label: 'Budget', icon: faMoneyBillWave },
  { key: 'documents', label: 'Documents', icon: faFileText },
  { key: 'checklists', label: 'Checklists', icon: faCheckCircle },
  { key: 'cautions', label: 'Cautions', icon: faExclamationTriangle },
  { key: 'brief', label: 'Discussion', icon: faComments },
  { key: 'attestation', label: 'Attest. Réf.', icon: faCertificate },
  { key: 'history', label: 'Historique', icon: faHistory },
];

const AO_TAB = { key: 'ai_assistant', label: 'Assistant IA', icon: faRobot };

// Tabs hidden per role. resp_controle has project access but is not part of the
// project team's discussion channel.
const HIDDEN_TABS_BY_ROLE = {
  resp_controle: ['brief'],
};

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const searchParams = new URLSearchParams(window.location.search);
  const initialTab = searchParams.get('tab') || 'overview';
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [briefSlots, setBriefSlots] = useState([]);
  const [editClients, setEditClients] = useState([]);
  const [editUsers, setEditUsers] = useState([]);
  const [stepsExpanded, setStepsExpanded] = useState(false);
  const [hasUnreadDiscussion, setHasUnreadDiscussion] = useState(false);
  const [archiveEligibility, setArchiveEligibility] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });

  const archiveDismiss = useModalDismiss(() => setShowArchiveModal(false), { enabled: showArchiveModal });

  const fetchProject = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const res = await api.get(`/projects/${id}`);
      setProject(res.data.data);
    } catch (error) {
      console.error('Error loading project:', error);
      if (error.response?.status === 404 || error.response?.status === 403) navigate('/projects');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  // Fetch archive eligibility when modal opens (don't clear old data to avoid flicker)
  useEffect(() => {
    if (!showArchiveModal || !id) return;
    api.get(`/projects/${id}/archive-eligibility`).then(res => setArchiveEligibility(res.data.data)).catch(() => {});
  }, [showArchiveModal, id]);

  useEffect(() => {
    if (!id) return;
    api.get(`/projects/${id}/slots`).then(res => {
      const data = res.data.data || {};
      const all = [
        ...(data.waves || []).flatMap(w => (w.survey_slots || []).map(s => ({ ...s, waveName: w.name }))),
        ...(data.standalone_slots || []).map(s => ({ ...s, waveName: null })),
      ];
      setBriefSlots(all);
    }).catch(() => {});
  }, [id]);

  // Poll for unread discussion messages (shows badge on Discussion tab).
  // Skipped for roles without the Discussion tab — nothing would consume it.
  useEffect(() => {
    if (!id) return;
    if ((HIDDEN_TABS_BY_ROLE[user?.role] || []).includes('brief')) return;
    const STORAGE_PREFIX = `project_${id}_read_`;
    const check = async () => {
      try {
        const res = await api.get(`/projects/${id}/messages/latest-per-channel`);
        const latest = res.data.data || {};
        let anyUnread = false;
        for (const [channelKey, latestId] of Object.entries(latest)) {
          const stored = parseInt(localStorage.getItem(`${STORAGE_PREFIX}${channelKey}`) || '0', 10);
          if (latestId > stored) { anyUnread = true; break; }
        }
        setHasUnreadDiscussion(anyUnread);
      } catch {}
    };
    check();
    const intervalId = setInterval(check, 10000); // check every 10s
    return () => clearInterval(intervalId);
  }, [id, user?.role]);

  const toDateInput = (v) => {
    if (!v) return '';
    // Fast path: already in YYYY-MM-DD form (no time component) → use as-is
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const d = new Date(v);
    if (isNaN(d)) return '';
    // Use LOCAL date parts (matches how Aperçu renders the date)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const toDateTimeInput = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d)) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const startEdit = () => {
    setEditForm({
      name: project.name,
      client_id: project.client_id || null,
      department: project.department || '',
      assignee_ids: (project.assignees || []).map(a => a.id),
      notes: project.notes || '',
      ao_deadline: toDateTimeInput(project.ao_deadline),
      setup_deadline: toDateInput(project.setup_deadline),
      terrain_deadline: toDateInput(project.terrain_deadline),
      reporting_deadline: toDateInput(project.reporting_deadline),
      closing_deadline: toDateInput(project.closing_deadline),
      solde_global: project.solde_global || '',
      date_debut: toDateInput(project.date_debut),
      date_fin: toDateInput(project.date_fin),
      livrables_deadline: toDateInput(project.livrables_deadline),
      reception_date: toDateInput(project.reception_date),
      facturation_date: toDateInput(project.facturation_date),
      attente_paiement_date: toDateInput(project.attente_paiement_date),
      payment_date: toDateInput(project.payment_date),
      attestation_email: project.attestation_email || '',
    });
    setActiveTab('overview');
    setEditing(true);
    // Fetch dropdown data
    api.get('/projects/clients').then(r => setEditClients(r.data.data || [])).catch(() => {});
    api.get('/projects/assignable-users').then(r => setEditUsers(r.data.data || [])).catch(() => {});
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${id}`, editForm);
      setEditing(false);
      fetchProject();
    } catch (error) {
      setErrorModal({ open: true, message: error.response?.data?.message || 'Erreur lors de la mise à jour' });
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    setArchiving(true);
    try {
      await api.post(`/projects/${id}/archive`);
      setShowArchiveModal(false);
      fetchProject();
      setArchiveEligibility(null);
    } catch (error) {
      setErrorModal({ open: true, message: error.response?.data?.message || 'Erreur' });
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchive = async () => {
    setArchiving(true);
    try {
      await api.post(`/projects/${id}/unarchive`);
      setShowUnarchiveModal(false);
      fetchProject();
      api.get(`/projects/${id}/archive-eligibility`).then(res => setArchiveEligibility(res.data.data)).catch(() => {});
    } catch (error) {
      setShowUnarchiveModal(false);
      setErrorModal({ open: true, message: error.response?.data?.message || 'Erreur' });
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
      </div>
    );
  }

  if (!project) {
    return <div style={{ textAlign: 'center', padding: 80, color: '#9ca3af' }}>Projet non trouvé</div>;
  }

  const isLocked = !!project.locked_at;
  const isArchived = !!project.archived_at;
  const isAO = ['appel_offre', 'consultation_restreinte'].includes(project.project_type);
  const baseTabs = HIDDEN_TABS_BY_ROLE[user?.role]
    ? BASE_TABS.filter(t => !HIDDEN_TABS_BY_ROLE[user.role].includes(t.key))
    : BASE_TABS;
  const TABS = isAO ? [baseTabs[0], AO_TAB, ...baseTabs.slice(1)] : baseTabs;
  const dept = DEPT_STYLE[project.department] || DEPT_STYLE.immersion;
  const stepIdx = STEPS_ORDER.indexOf(project.step);

  return (
    <div className="project-detail-page">
      {/* ── Header ── */}
      <div style={{ marginBottom: 20 }}>
        {/* Badges row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.68rem', fontWeight: 600, padding: '2px 10px', borderRadius: 10, background: dept.bg, color: dept.color }}>
            {dept.label}
          </span>
          <span style={{ fontSize: '0.68rem', fontWeight: 500, padding: '2px 10px', borderRadius: 10, border: '1px solid #d1d5db', color: '#4b5563' }}>
            {TYPE_LABELS[project.project_type]}
          </span>
          {isLocked && (
            <span style={{ fontSize: '0.68rem', fontWeight: 600, padding: '2px 10px', borderRadius: 10, background: '#fee2e2', color: '#991b1b' }}>
              <FontAwesomeIcon icon={faLock} size="xs" className="me-1" />Verrouillé
            </span>
          )}
          {project.archived_at && (
            <span style={{ fontSize: '0.68rem', fontWeight: 600, padding: '2px 10px', borderRadius: 10, background: '#f3f4f6', color: '#6b7280' }}>
              <FontAwesomeIcon icon={faArchive} size="xs" className="me-1" />Archivé
            </span>
          )}
        </div>

        {/* Title + client */}
        <div>
          <h4 style={{ fontWeight: 700, fontSize: '1.25rem', margin: 0, color: '#111827' }}>
            {project.name}
          </h4>
          {project.client && (
            <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: 2 }}>
              <FontAwesomeIcon icon={faBuilding} size="xs" className="me-1" />
              {project.client.name}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          {!project.archived_at && !editing && (
            <>
              {!isLocked && (
                <button onClick={startEdit} className="project-detail-btn">
                  <FontAwesomeIcon icon={faEdit} size="sm" className="me-1" />Modifier
                </button>
              )}
              <button onClick={() => setShowArchiveModal(true)} className="project-detail-btn" style={{ color: '#6b7280' }}>
                <FontAwesomeIcon icon={faArchive} size="sm" className="me-1" />Archiver
              </button>
            </>
          )}
          {project.archived_at && (
            <button onClick={() => setShowUnarchiveModal(true)} className="project-detail-btn">
              <FontAwesomeIcon icon={faBoxOpen} size="sm" className="me-1" />Désarchiver
            </button>
          )}
        </div>

        {/* ── Step progress bar — clickable ── */}
        {(() => {
          const slotSteps = ['terrain', 'reporting', 'closing'];
          const slotCounts = {};
          slotSteps.forEach(s => { slotCounts[s] = briefSlots.filter(sl => sl.step === s).length; });
          const hasSlots = briefSlots.length > 0;
          const slotsByStep = {};
          slotSteps.forEach(s => { slotsByStep[s] = briefSlots.filter(sl => sl.step === s); });

          return (
            <div style={{ marginTop: 16 }}>
              <div
                onClick={() => hasSlots && setStepsExpanded(prev => !prev)}
                className="project-detail-steps-bar"
                style={{ cursor: hasSlots ? 'pointer' : 'default' }}
              >
                {STEPS_ORDER.map((step, i) => {
                  const isSlotStep = slotSteps.includes(step);
                  const accent = STEP_ACCENT[step];
                  const isPast = i < stepIdx;
                  const isActive = step === project.step;
                  const slotCount = slotCounts[step] || 0;
                  const hasActiveSlots = isSlotStep && slotCount > 0;

                  let barBg = '#e5e7eb', barOpacity = 1;
                  if (isSlotStep) { if (hasActiveSlots) barBg = accent; }
                  else { if (isPast) { barBg = accent; barOpacity = 0.5; } else if (isActive) barBg = accent; }

                  let labelColor = '#d1d5db', labelWeight = 500;
                  if (isSlotStep) { if (hasActiveSlots) { labelColor = accent; labelWeight = 600; } }
                  else { if (isActive) { labelColor = accent; labelWeight = 700; } else if (isPast) labelColor = '#9ca3af'; }

                  return (
                    <div key={step} style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{ height: 4, borderRadius: 2, marginBottom: 4, background: barBg, opacity: barOpacity, transition: 'background 0.3s' }} />
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                        <span style={{ fontSize: '0.62rem', fontWeight: labelWeight, color: labelColor, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                          {STEP_LABELS[step]}
                        </span>
                        {isSlotStep && hasSlots && (
                          <span style={{
                            fontSize: '0.52rem', fontWeight: 700, minWidth: 14, textAlign: 'center',
                            padding: '0 3px', borderRadius: 6, lineHeight: '14px',
                            background: hasActiveSlots ? accent + '20' : '#f3f4f6',
                            color: hasActiveSlots ? accent : '#d1d5db',
                          }}>
                            {slotCount}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Expanded slot details */}
              {stepsExpanded && hasSlots && (
                <div style={{
                  marginTop: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                  overflow: 'hidden', animation: 'stepsExpandIn 0.2s ease',
                }}>
                  <div className="project-detail-slot-grid">
                    {slotSteps.map(step => {
                      const accent = STEP_ACCENT[step];
                      const slots = slotsByStep[step] || [];
                      return (
                        <div key={step} className="project-detail-slot-col">
                          <div style={{
                            padding: '8px 12px', borderBottom: '1px solid #f3f4f6',
                            borderTop: `3px solid ${slots.length > 0 ? accent : '#e5e7eb'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          }}>
                            <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                              {STEP_LABELS[step]}
                            </span>
                            <span style={{ fontSize: '0.55rem', color: '#9ca3af' }}>{slots.length}</span>
                          </div>
                          <div style={{ padding: '6px 10px', minHeight: 40 }}>
                            {slots.length === 0 ? (
                              <div style={{ fontSize: '0.68rem', color: '#e5e7eb', fontStyle: 'italic', padding: '8px 0', textAlign: 'center' }}>—</div>
                            ) : slots.map(sl => (
                              <div key={sl.id} style={{
                                display: 'flex', alignItems: 'center', gap: 6, padding: '5px 4px',
                                borderBottom: '1px solid #f9fafb',
                              }}>
                                <div style={{
                                  width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                                  background: sl.is_finished ? '#10b981' : accent,
                                }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{
                                    fontSize: '0.7rem', fontWeight: 500, color: '#111827',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  }}>
                                    {sl.name}
                                  </div>
                                  {sl.survey && (
                                    <div style={{ fontSize: '0.58rem', color: '#9ca3af' }}>
                                      {sl.survey.title}
                                      {(sl.linked_surveys?.length || 0) > 1 && (
                                        <span style={{ color: '#7c3aed', fontWeight: 600 }}> +{sl.linked_surveys.length - 1}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {sl.is_finished && (
                                  <span style={{ fontSize: '0.5rem', fontWeight: 700, padding: '1px 4px', borderRadius: 4, background: '#dcfce7', color: '#166534', flexShrink: 0 }}>
                                    Terminée
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        <style>{`@keyframes stepsExpandIn { from { opacity: 0; max-height: 0; } to { opacity: 1; max-height: 500px; } }`}</style>
      </div>

      {/* ── Tabs ── */}
      <div className="project-detail-tabs-wrap">
        <div className="project-detail-tabs">
          {TABS.map(tab => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); window.history.replaceState(null, '', `/projects/${id}?tab=${tab.key}`); }}
                className={`project-detail-tab${active ? ' active' : ''}`}
              >
                <FontAwesomeIcon icon={tab.icon} size="sm" className="me-1" style={{ opacity: 0.7 }} />
                {tab.label}
                {tab.key === 'brief' && hasUnreadDiscussion && (
                  <span style={{
                    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                    background: '#dc2626', marginLeft: 6, verticalAlign: 'middle',
                    animation: 'unreadPulse 1.5s ease-in-out infinite',
                  }} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Tab content ── */}
      {activeTab === 'overview' && (
        <OverviewTab
          project={project}
          projectId={id}
          onRefresh={fetchProject}
          editing={editing}
          editForm={editForm}
          setEditForm={setEditForm}
          saving={saving}
          onSave={handleSave}
          onCancel={() => setEditing(false)}
          clients={editClients}
          setClients={setEditClients}
          assignableUsers={editUsers}
          onError={(msg) => setErrorModal({ open: true, message: msg })}
          hasSlots={briefSlots.length > 0}
        />
      )}
      {activeTab === 'budget' && <BudgetTab projectId={project.id} project={project} readOnly={isArchived} />}
      {activeTab === 'documents' && <DocumentsTab projectId={project.id} currentStep={project.step} readOnly={isArchived} />}
      {activeTab === 'checklists' && <ChecklistTab projectId={project.id} readOnly={isArchived} />}
      {activeTab === 'surveys' && (
        <SurveySlotsTab projectId={project.id} readOnly={isArchived} />
      )}
      {activeTab === 'cautions' && <CautionsTab projectId={project.id} readOnly={isArchived} />}
      {activeTab === 'brief' && TABS.some(t => t.key === 'brief') && <BriefTab projectId={project.id} slots={briefSlots} onUnreadChange={setHasUnreadDiscussion} readOnly={isArchived} />}
      {activeTab === 'ai_assistant' && <AOTab projectId={project.id} readOnly={isArchived} />}
      {activeTab === 'attestation' && <AttestationTab projectId={id} project={project} readOnly={isArchived} />}
      {activeTab === 'history' && <HistoryTab history={project.step_history || []} />}

      {/* Archive confirmation modal */}
      {showArchiveModal && !archiveEligibility && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div {...archiveDismiss} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white" style={{ position: 'relative', zIndex: 1 }} />
        </div>
      )}
      {showArchiveModal && archiveEligibility && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div {...archiveDismiss} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
          <div style={{ position: 'relative', background: '#fff', borderRadius: 12, padding: '28px 32px', width: 440, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h5 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
              <FontAwesomeIcon icon={faArchive} className="me-2" style={{ color: '#6b7280' }} />
              Archiver ce projet ?
            </h5>

            {!archiveEligibility.can_archive ? (
              <>
                <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 12px' }}>
                  Ce projet ne peut pas encore être archivé :
                </p>
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                  {archiveEligibility.reasons.map((r, i) => (
                    <div key={i} style={{ fontSize: '0.78rem', color: '#991b1b', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FontAwesomeIcon icon={faExclamationTriangle} size="xs" />
                      {r}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowArchiveModal(false)}
                    style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
                    Fermer
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 20px' }}>
                  Le projet sera déplacé dans les archives. Il n'apparaîtra plus dans le Kanban. Vous pourrez le désarchiver à tout moment.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setShowArchiveModal(false)}
                    style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
                    Annuler
                  </button>
                  <button onClick={handleArchive} disabled={archiving}
                    style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#374151', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', opacity: archiving ? 0.5 : 1 }}>
                    {archiving ? 'Archivage...' : 'Archiver'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showUnarchiveModal}
        onClose={() => setShowUnarchiveModal(false)}
        onConfirm={handleUnarchive}
        title="Désarchiver ce projet ?"
        message="Le projet sera restauré et apparaîtra à nouveau dans le Kanban."
        variant="info"
        confirmLabel="Désarchiver"
        loading={archiving}
      />

      <ConfirmModal
        isOpen={errorModal.open}
        onClose={() => setErrorModal({ open: false, message: '' })}
        title="Erreur"
        message={errorModal.message}
        variant="danger"
      />

      <style>{`
        @keyframes unreadPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.75); }
        }
        .project-detail-page {
          padding: 16px 24px;
          max-width: 1200px;
          margin: 0 auto;
        }
        .project-detail-btn {
          padding: 6px 14px;
          border-radius: 6px;
          border: 1px solid #d1d5db;
          background: #fff;
          color: #374151;
          font-size: 0.78rem;
          font-weight: 500;
          cursor: pointer;
          white-space: nowrap;
        }
        .project-detail-steps-bar {
          display: flex;
          gap: 4px;
        }
        .project-detail-slot-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
        }
        .project-detail-slot-col {
          border-right: 1px solid #f3f4f6;
        }
        .project-detail-slot-col:last-child {
          border-right: none;
        }
        .project-detail-tabs-wrap {
          position: relative;
          margin-bottom: 20px;
        }
        .project-detail-tabs {
          display: flex;
          gap: 0;
          border-bottom: 1px solid #e5e7eb;
          overflow-x: auto;
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .project-detail-tabs::-webkit-scrollbar { display: none; }
        .project-detail-tab {
          padding: 10px 16px;
          border: none;
          cursor: pointer;
          background: transparent;
          font-size: 0.78rem;
          font-weight: 500;
          color: #6b7280;
          border-bottom: 2px solid transparent;
          transition: all 0.15s;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .project-detail-tab.active {
          font-weight: 600;
          color: #dc2626;
          border-bottom-color: #dc2626;
        }
        .project-detail-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
        }
        .project-detail-deadlines-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }
        .project-detail-overview-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .project-detail-metrics-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 12px;
          margin-bottom: 12px;
        }
        @media (max-width: 768px) {
          .project-detail-page {
            padding: 12px 12px;
          }
          .project-detail-steps-bar {
            gap: 2px;
          }
          .project-detail-steps-bar span {
            font-size: 0.52rem !important;
            letter-spacing: 0 !important;
          }
          .project-detail-slot-grid {
            grid-template-columns: 1fr;
          }
          .project-detail-slot-col {
            border-right: none;
            border-bottom: 1px solid #f3f4f6;
          }
          .project-detail-slot-col:last-child {
            border-bottom: none;
          }
          .project-detail-tabs {
            scroll-snap-type: x mandatory;
            scroll-padding: 0 16px;
          }
          .project-detail-tab {
            scroll-snap-align: start;
            padding: 10px 14px;
            font-size: 0.72rem;
          }
          .project-detail-tabs-wrap::before,
          .project-detail-tabs-wrap::after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            width: 24px;
            z-index: 1;
            pointer-events: none;
          }
          .project-detail-tabs-wrap::before {
            left: 0;
            background: linear-gradient(90deg, #fff 0%, transparent 100%);
          }
          .project-detail-tabs-wrap::after {
            right: 0;
            background: linear-gradient(270deg, #fff 0%, transparent 100%);
          }
          .project-detail-form-row {
            grid-template-columns: 1fr;
          }
          .project-detail-deadlines-grid {
            grid-template-columns: 1fr;
          }
          .project-detail-overview-grid {
            grid-template-columns: 1fr;
          }
          .project-detail-metrics-grid {
            grid-template-columns: 1fr;
          }
          .history-entry {
            padding: 10px 12px !important;
            gap: 10px !important;
          }
          .history-entry .notes-text {
            word-break: break-word;
          }
        }
        .history-entry {
          display: flex; align-items: flex-start; gap: 14px; padding: 12px 20px;
        }
      `}</style>
    </div>
  );
};

// ── Overview Tab ──

const DEPT_OPTIONS = [
  { value: 'define', label: 'Define', color: '#1e40af', bg: '#dbeafe' },
  { value: 'voa', label: 'Voa', color: '#a16207', bg: '#fef9c3' },
  { value: 'immersion', label: 'Immersion', color: '#991b1b', bg: '#fee2e2' },
];

const OverviewTab = ({ project, projectId, onRefresh, editing, editForm, setEditForm, saving, onSave, onCancel, clients, setClients, assignableUsers, onError, hasSlots }) => {
  const [aoSaving, setAoSaving] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const isArchived = !!project.archived_at;

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/projects/${projectId}`, { notes: notesValue });
      setEditingNotes(false);
      onRefresh({ silent: true });
    } catch (err) {
      if (onError) onError(err.response?.data?.message || 'Erreur');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleAoResult = async (result) => {
    setAoSaving(true);
    try {
      await api.post(`/projects/${projectId}/ao-result`, { ao_result: result });
      onRefresh({ silent: true });
    } catch (err) {
      if (onError) onError(err.response?.data?.message || 'Erreur');
    } finally {
      setAoSaving(false);
    }
  };

  if (editing) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20 }}>
        {/* Name */}
        <div style={{ marginBottom: 16 }}>
          <FieldLabel>Nom du projet</FieldLabel>
          <input className="detail-input" type="text" value={editForm.name}
            onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))} />
        </div>

        {/* Client + Department */}
        <div className="project-detail-form-row">
          <div>
            <FieldLabel>Client</FieldLabel>
            <SearchableSelect
              options={clients || []}
              selectedValue={editForm.client_id}
              onChange={v => setEditForm(prev => ({ ...prev, client_id: v }))}
              placeholder="Sélectionner un client..."
              getOptionLabel={c => c.name}
              getOptionMeta={() => null}
              triggerClassName="px-3 py-2"
            />
            {/* Inline abbreviation editor for the selected client */}
            {editForm.client_id && (() => {
              const sel = (clients || []).find(c => c.id === editForm.client_id);
              if (!sel) return null;
              return (
                <div style={{ marginTop: 6 }}>
                  <input className="detail-input" type="text" maxLength={20}
                    defaultValue={sel.abbreviation || ''}
                    placeholder="Abréviation (auto si vide)"
                    style={{ fontSize: '0.75rem' }}
                    onBlur={async (e) => {
                      const val = e.target.value.trim();
                      if (val === (sel.abbreviation || '')) return;
                      try {
                        const res = await api.put(`/projects/clients/${sel.id}`, { abbreviation: val || null });
                        const updated = res.data.data;
                        setClients(prev => prev.map(c => c.id === updated.id ? updated : c));
                      } catch { }
                    }}
                  />
                  <div style={{ fontSize: '0.62rem', color: '#9ca3af', marginTop: 2 }}>
                    Auto si vide (initiales en excluant et, de, du, des, le, la, les)
                  </div>
                </div>
              );
            })()}
          </div>
          <div>
            <FieldLabel>Département</FieldLabel>
            <div style={{ display: 'flex', gap: 6 }}>
              {DEPT_OPTIONS.map(d => (
                <button key={d.value} type="button" onClick={() => setEditForm(prev => ({ ...prev, department: d.value }))}
                  style={{
                    flex: 1, padding: '7px 0', borderRadius: 6, cursor: 'pointer',
                    border: editForm.department === d.value ? `2px solid ${d.color}` : '1px solid #d1d5db',
                    background: editForm.department === d.value ? d.bg : '#fff',
                    color: editForm.department === d.value ? d.color : '#6b7280',
                    fontWeight: editForm.department === d.value ? 700 : 500,
                    fontSize: '0.75rem', transition: 'all 0.15s',
                  }}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Assignees */}
        <div style={{ marginBottom: 16 }}>
          <FieldLabel>Personnes assignées</FieldLabel>
          <SearchableMultiSelect
            options={assignableUsers || []}
            selectedValues={editForm.assignee_ids || []}
            onChange={ids => setEditForm(prev => ({ ...prev, assignee_ids: ids }))}
            placeholder="Ajouter des personnes..."
            getOptionLabel={u => u.name}
            getOptionMeta={u => u.role_display || u.role}
            triggerClassName="px-3 py-2"
          />
        </div>

        {/* Dates */}
        <div className="project-detail-form-row">
          <div>
            <FieldLabel>Date début</FieldLabel>
            <input className="detail-input" type="date" value={editForm.date_debut || ''}
              onChange={e => setEditForm(prev => ({ ...prev, date_debut: e.target.value }))} />
          </div>
          <div>
            <FieldLabel>Date fin</FieldLabel>
            <input className="detail-input" type="date" value={editForm.date_fin || ''}
              onChange={e => setEditForm(prev => ({ ...prev, date_fin: e.target.value }))} />
          </div>
        </div>

        {/* Solde (Contrat) */}
        {project.project_type === 'contrat' && (
          <div style={{ marginBottom: 16 }}>
            <FieldLabel>Solde global (MAD)</FieldLabel>
            <input className="detail-input" type="number" value={editForm.solde_global}
              onChange={e => setEditForm(prev => ({ ...prev, solde_global: e.target.value }))}
              min="0" step="0.01" />
          </div>
        )}

        {/* Échéances */}
        <div style={{ marginBottom: 16 }}>
          <FieldLabel>Échéances par étape</FieldLabel>
          <div className="project-detail-deadlines-grid">
            {STEPS_ORDER.filter(s => !['terrain', 'reporting'].includes(s)).map(step => (
              <div key={step}>
                <div style={{ fontSize: '0.62rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>
                  {STEP_LABELS[step]}{step === 'ao' ? ' (date & heure)' : ''}
                </div>
                <input className="detail-input"
                  type={step === 'ao' ? 'datetime-local' : 'date'}
                  value={editForm[`${step}_deadline`] || ''}
                  onChange={e => setEditForm(prev => ({ ...prev, [`${step}_deadline`]: e.target.value }))}
                  style={{ fontSize: '0.75rem' }} />
              </div>
            ))}
          </div>
        </div>

        {/* Dates closing — only for projects without slots */}
        {!hasSlots && (
          <div style={{ marginBottom: 16 }}>
            <FieldLabel>Dates closing</FieldLabel>
            <div className="project-detail-deadlines-grid">
              {[
                ['livrables_deadline', 'Échéance livrables'],
                ['reception_date', 'Réception'],
                ['facturation_date', 'Facturation'],
                ['attente_paiement_date', 'En attente paiement'],
                ['payment_date', 'Paiement (encaissé)'],
              ].map(([key, label]) => {
                // La date de facturation est posée automatiquement en cochant « Facturation » ;
                // elle ne se corrige qu'ensuite, en attente de paiement ou une fois encaissé.
                const locked = key === 'facturation_date'
                  && !['attente_paiement', 'encaisse'].includes(project.closing_step);
                return (
                  <div key={key}>
                    <div style={{ fontSize: '0.62rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
                    <input className="detail-input" type="date" value={editForm[key] || ''}
                      onChange={e => setEditForm(prev => ({ ...prev, [key]: e.target.value }))}
                      disabled={locked}
                      style={{ fontSize: '0.75rem', background: locked ? '#f9fafb' : undefined, color: locked ? '#9ca3af' : undefined }} />
                    {locked && (
                      <div style={{ fontSize: '0.62rem', color: '#9ca3af', marginTop: 3 }}>
                        Modifiable en attente de paiement ou encaissé.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Attestation email */}
        <div style={{ marginBottom: 16 }}>
          <FieldLabel>Email attestation de référence</FieldLabel>
          <input className="detail-input" type="email" value={editForm.attestation_email || ''}
            onChange={e => setEditForm(prev => ({ ...prev, attestation_email: e.target.value }))}
            placeholder="contact@client.com" />
          <div style={{ fontSize: '0.66rem', color: '#9ca3af', marginTop: 4 }}>
            Utilisé par défaut pour toutes les enquêtes du projet (peut être surchargé par enquête)
          </div>
        </div>

        {/* Notes */}
        <div style={{ marginBottom: 16 }}>
          <FieldLabel>Notes</FieldLabel>
          <textarea className="detail-input" rows={3} value={editForm.notes}
            onChange={e => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
            style={{ resize: 'vertical' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onCancel} disabled={saving} className="detail-btn detail-btn-secondary">
            <FontAwesomeIcon icon={faTimes} size="sm" className="me-1" />Annuler
          </button>
          <button onClick={onSave} disabled={saving} className="detail-btn detail-btn-primary">
            <FontAwesomeIcon icon={faSave} size="sm" className="me-1" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>

        <style>{detailStyles}</style>
      </div>
    );
  }

  const dept = DEPT_STYLE[project.department] || DEPT_STYLE.immersion;

  return (
    <div className="project-detail-overview-grid">
      {/* Left: info */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card title="Informations">
          <InfoRow icon={faBuilding} label="Client" value={project.client?.name} />
          <InfoRow icon={null} label="Département">
            <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '1px 8px', borderRadius: 10, background: dept.bg, color: dept.color }}>
              {dept.label}
            </span>
          </InfoRow>
          <InfoRow icon={null} label="Type" value={TYPE_LABELS[project.project_type]} />
          <InfoRow icon={faUser} label={(project.assignees && project.assignees.length > 1) ? "Chargé(e)s d'étude" : "Chargé(e) d'étude"}
            value={(project.assignees && project.assignees.length > 0) ? project.assignees.map(a => a.name).join(', ') : (project.assigned_to?.name || '—')} />
          {(project.date_debut || project.date_fin) && (
            <InfoRow icon={faCalendarAlt} label="Période"
              value={`${project.date_debut ? new Date(project.date_debut).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'} → ${project.date_fin ? new Date(project.date_fin).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}`} />
          )}
          <InfoRow icon={null} label="Email client (attest. réf)" value={project.attestation_email || '—'} />
        </Card>

        {/* AO Result — only for projects in AO step */}
        {project.step === 'ao' && (
          <div style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden',
          }}>
            {(() => {
              const isCR = project.project_type === 'consultation_restreinte';
              const resultTitle = isCR ? 'Résultat de la consultation' : 'Résultat AO';
              const wonLabel = isCR ? 'Retenue' : 'Gagné';
              const lostLabel = isCR ? 'Non retenue' : 'Perdu';
              const wonTitle = isCR ? 'Consultation retenue' : 'AO Gagné';
              const lostTitle = isCR ? 'Consultation non retenue' : 'AO Perdu';
              return (<>
            <div style={{
              padding: '10px 16px', borderBottom: '1px solid #f3f4f6',
              fontSize: '0.72rem', fontWeight: 700, color: '#6b7280',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              {resultTitle}
            </div>
            <div style={{ padding: '16px' }}>
              {!project.ao_result || project.ao_result === 'pending' ? (
                <div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleAoResult('won')}
                      disabled={aoSaving}
                      style={{
                        flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer',
                        border: '2px solid #10b981', background: '#f0fdf4', color: '#166534',
                        fontSize: '0.82rem', fontWeight: 600, transition: 'all 0.15s',
                      }}
                    >
                      {wonLabel}
                    </button>
                    <button
                      onClick={() => handleAoResult('lost')}
                      disabled={aoSaving}
                      style={{
                        flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer',
                        border: '2px solid #ef4444', background: '#fef2f2', color: '#991b1b',
                        fontSize: '0.82rem', fontWeight: 600, transition: 'all 0.15s',
                      }}
                    >
                      {lostLabel}
                    </button>
                  </div>
                </div>
              ) : project.ao_result === 'won' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', background: '#dcfce7',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#16a34a' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#166534' }}>{wonTitle}</div>
                    <div style={{ fontSize: '0.68rem', color: '#6b7280' }}>
                      Vous pouvez passer le projet en Setup
                    </div>
                  </div>
                  <button
                    onClick={() => handleAoResult('pending')}
                    disabled={aoSaving}
                    style={{
                      marginLeft: 'auto', fontSize: '0.68rem', padding: '3px 8px', borderRadius: 6,
                      border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', cursor: 'pointer',
                    }}
                  >
                    Modifier
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', background: '#fee2e2',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <FontAwesomeIcon icon={faTimes} style={{ color: '#dc2626' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#991b1b' }}>{lostTitle}</div>
                    <div style={{ fontSize: '0.68rem', color: '#6b7280' }}>
                      {isCR ? 'Le projet ne sera pas retenu' : 'Pensez à récupérer la caution provisoire'}
                    </div>
                  </div>
                  <button
                    onClick={() => handleAoResult('pending')}
                    disabled={aoSaving}
                    style={{
                      marginLeft: 'auto', fontSize: '0.68rem', padding: '3px 8px', borderRadius: 6,
                      border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', cursor: 'pointer',
                    }}
                  >
                    Modifier
                  </button>
                </div>
              )}
            </div>
              </>);
            })()}
          </div>
        )}

        <Card title="Notes" action={
          !isArchived && !editingNotes ? (
            <button onClick={() => { setNotesValue(project.notes || ''); setEditingNotes(true); }}
              style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.7rem', fontWeight: 500, cursor: 'pointer' }}>
              <FontAwesomeIcon icon={project.notes ? faEdit : faPlus} size="xs" className="me-1" />{project.notes ? 'Modifier' : 'Ajouter'}
            </button>
          ) : null
        }>
          {editingNotes ? (
            <div>
              <textarea value={notesValue} onChange={e => setNotesValue(e.target.value)} rows={4} autoFocus placeholder="Ajouter une note..."
                style={{ width: '100%', fontSize: '0.82rem', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <button onClick={() => setEditingNotes(false)} disabled={savingNotes}
                  style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>Annuler</button>
                <button onClick={handleSaveNotes} disabled={savingNotes}
                  style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', opacity: savingNotes ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {savingNotes && <FontAwesomeIcon icon={faSpinner} spin size="xs" />}Enregistrer
                </button>
              </div>
            </div>
          ) : (
            project.notes
              ? <div style={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{project.notes}</div>
              : <div style={{ fontSize: '0.78rem', color: '#d1d5db' }}>Aucune note</div>
          )}
        </Card>

        {/* Dates closing — projects without slots in closing */}
        {!hasSlots && project.step === 'closing' && (
          <Card title="Dates closing">
            {[
              ['Échéance livrables', project.livrables_deadline],
              ['Réception', project.reception_date],
              ['Facturation', project.facturation_date],
              ['En attente de paiement', project.attente_paiement_date],
              ['Paiement (encaissé)', project.payment_date],
            ].map(([label, val]) => (
              <InfoRow key={label} icon={null} label={label}
                value={val ? new Date(val).toLocaleDateString('fr-FR') : '—'} />
            ))}

            {['facturation', 'attente_paiement'].includes(project.closing_step) && project.payment_relance && (
              <div style={{ marginTop: 14 }}>
                <PaymentRelanceCard
                  projectId={projectId}
                  state={project.payment_relance}
                  canEdit={!!project.can_set_payment_relance && !isArchived}
                  onSaved={() => onRefresh({ silent: true })}
                />
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Right: deadlines + solde */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card title="Échéances">
          {STEPS_ORDER.map(step => {
            const deadline = project[`${step}_deadline`];
            const isCurrent = project.step === step;
            const accent = STEP_ACCENT[step];
            return (
              <div key={step} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 0', borderBottom: '1px solid #f3f4f6',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isCurrent && <div style={{ width: 6, height: 6, borderRadius: '50%', background: accent }} />}
                  <span style={{
                    fontSize: '0.8rem', fontWeight: isCurrent ? 700 : 500,
                    color: isCurrent ? accent : '#6b7280',
                    textTransform: 'uppercase', letterSpacing: '0.02em',
                  }}>
                    {STEP_LABELS[step]}
                  </span>
                </div>
                <span style={{
                  fontSize: '0.8rem', color: deadline ? '#374151' : '#d1d5db',
                  fontWeight: isCurrent ? 600 : 400,
                }}>
                  {deadline ? (step === 'ao'
                    ? new Date(deadline).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' + new Date(deadline).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                    : new Date(deadline).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })) : '—'}
                </span>
              </div>
            );
          })}
        </Card>

        {project.project_type === 'contrat' && project.solde_global && (
          <Card title="Solde Contrat">
            <div className="project-detail-metrics-grid">
              <MetricBox label="Global" value={`${Number(project.solde_global).toLocaleString('fr-FR')}`} unit="MAD" />
              <MetricBox label="Consommé" value={`${Number(project.solde_consumed || 0).toLocaleString('fr-FR')}`} unit="MAD" />
              <MetricBox
                label="Restant"
                value={`${Number(project.solde_remaining || 0).toLocaleString('fr-FR')}`}
                unit="MAD"
                color={Number(project.solde_remaining || 0) < 0 ? '#ef4444' : '#10b981'}
              />
            </div>
            <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 3, transition: 'width 0.3s',
                width: `${Math.min(100, ((project.solde_consumed || 0) / project.solde_global) * 100)}%`,
                background: ((project.solde_consumed || 0) / project.solde_global) > 0.9 ? '#ef4444' : '#dc2626',
              }} />
            </div>
            <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 4, textAlign: 'right' }}>
              {Math.round(((project.solde_consumed || 0) / project.solde_global) * 100)}% consommé
            </div>
          </Card>
        )}
      </div>

      <style>{detailStyles}</style>
    </div>
  );
};

// ── History Tab ──

const ACTION_STYLE = {
  slots_created:              { label: 'Enquêtes ajoutées',        color: '#2563eb', bg: '#dbeafe' },
  slot_updated:               { label: 'Enquête modifiée',         color: '#6b7280', bg: '#f3f4f6' },
  slot_deleted:               { label: 'Enquête supprimée',        color: '#dc2626', bg: '#fef2f2' },
  survey_linked:              { label: 'Enquête liée',             color: '#16a34a', bg: '#dcfce7' },
  survey_unlinked:            { label: 'Enquête déliée',           color: '#d97706', bg: '#fef9c3' },
  survey_modified:            { label: 'Enquête modifiée',         color: '#7c3aed', bg: '#f3e8ff' },
  slot_finished:              { label: 'Terminée',                 color: '#16a34a', bg: '#dcfce7' },
  slot_unfinished:            { label: 'Non terminée',             color: '#d97706', bg: '#fef9c3' },
  caution_recovered:          { label: 'Caution récupérée',        color: '#16a34a', bg: '#dcfce7' },
  caution_mainlevee:          { label: 'Mainlevée',                color: '#6b7280', bg: '#f3f4f6' },
  closing_step_changed:       { label: 'Étape closing modifiée',   color: '#7c3aed', bg: '#f3e8ff' },
  attestation_choice:         { label: 'Choix attestation',        color: '#ea580c', bg: '#fff7ed' },
  attestation_request_sent:   { label: 'Demande attestation envoyée', color: '#ea580c', bg: '#fff7ed' },
  payment_relance_set:        { label: 'Délai de paiement modifié',  color: '#1d4ed8', bg: '#dbeafe' },
  closing_date_changed:       { label: 'Date closing modifiée',      color: '#1d4ed8', bg: '#dbeafe' },
  ao_checklist_generated:     { label: 'Checklist AO générée',     color: '#1d4ed8', bg: '#dbeafe' },
  budget_approved:            { label: 'Budget validé',            color: '#16a34a', bg: '#dcfce7' },
  budget_rejected:            { label: 'Budget rejeté',            color: '#dc2626', bg: '#fef2f2' },
  budget_submitted:           { label: 'Budget soumis',            color: '#2563eb', bg: '#dbeafe' },
};

// Fallback formatter: snake_case → "Snake case" if no explicit label is defined
const humanizeAction = (a) => a ? a.split('_').map((w, i) => i === 0 ? (w.charAt(0).toUpperCase() + w.slice(1)) : w).join(' ') : '';

const HistoryTab = ({ history }) => {
  if (!history || history.length === 0) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '40px 20px', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
        Aucun historique disponible.
      </div>
    );
  }

  return (
    <div className="history-tab-list" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
      {history.map((entry, i) => {
        const isStepTransition = entry.to_step && !entry.action;
        const actionStyle = entry.action ? (ACTION_STYLE[entry.action] || { label: humanizeAction(entry.action), color: '#6b7280', bg: '#f3f4f6' }) : null;
        const toAccent = isStepTransition ? (STEP_ACCENT[entry.to_step] || '#6366f1') : (actionStyle?.color || '#6b7280');

        return (
          <div key={entry.id} className="history-entry" style={{
            borderBottom: i < history.length - 1 ? '1px solid #f3f4f6' : 'none',
          }}>
            {/* Timeline dot */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4, flexShrink: 0 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: toAccent }} />
              {i < history.length - 1 && <div style={{ width: 1, height: 24, background: '#e5e7eb', marginTop: 4 }} />}
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {isStepTransition ? (
                  <>
                    {entry.from_step && (
                      <>
                        <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '1px 8px', borderRadius: 10, background: '#f3f4f6', color: '#6b7280' }}>
                          {STEP_LABELS[entry.from_step] || entry.from_step}
                        </span>
                        <span style={{ color: '#d1d5db', fontSize: '0.7rem' }}>→</span>
                      </>
                    )}
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '1px 8px', borderRadius: 10, background: toAccent + '20', color: toAccent }}>
                      {STEP_LABELS[entry.to_step] || entry.to_step}
                    </span>
                  </>
                ) : actionStyle ? (
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '1px 8px', borderRadius: 10, background: actionStyle.bg, color: actionStyle.color }}>
                    {actionStyle.label}
                  </span>
                ) : null}
              </div>
              {entry.notes && (
                <div style={{ fontSize: '0.78rem', color: '#374151', marginTop: 3 }}>{entry.notes}</div>
              )}
              <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 2 }}>
                {entry.changed_by?.name || '—'} · {new Date(entry.created_at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Shared sub-components ──

const Card = ({ title, children, action }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
    <div style={{
      padding: '10px 16px', borderBottom: '1px solid #f3f4f6',
      fontSize: '0.72rem', fontWeight: 700, color: '#6b7280',
      textTransform: 'uppercase', letterSpacing: '0.04em',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <span>{title}</span>
      {action}
    </div>
    <div style={{ padding: '8px 16px' }}>{children}</div>
  </div>
);

const InfoRow = ({ icon, label, value, children }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 0', borderBottom: '1px solid #f3f4f6',
    fontSize: '0.82rem',
  }}>
    <span style={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon && <FontAwesomeIcon icon={icon} size="xs" style={{ opacity: 0.5 }} />}
      {label}
    </span>
    {children || <span style={{ fontWeight: 500, color: value ? '#111827' : '#d1d5db' }}>{value || '—'}</span>}
  </div>
);

const MetricBox = ({ label, value, unit, color }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>
      {label}
    </div>
    <div style={{ fontSize: '1rem', fontWeight: 700, color: color || '#111827' }}>
      {value}
    </div>
    <div style={{ fontSize: '0.62rem', color: '#9ca3af' }}>{unit}</div>
  </div>
);

const FieldLabel = ({ children }) => (
  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 5 }}>
    {children}
  </div>
);

const detailStyles = `
  .detail-input {
    width: 100%;
    font-size: 0.82rem;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    outline: none;
    background: #fff;
    color: #111827;
    transition: border-color 0.15s;
  }
  .detail-input:focus {
    border-color: #dc2626;
  }
  .detail-btn {
    padding: 6px 14px;
    border-radius: 6px;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: all 0.15s;
  }
  .detail-btn-primary {
    background: #dc2626;
    color: #fff;
  }
  .detail-btn-primary:hover {
    background: #b91c1c;
  }
  .detail-btn-secondary {
    background: #fff;
    color: #374151;
    border: 1px solid #d1d5db;
  }
`;

export default ProjectDetail;
